import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Save, RotateCcw, Coins } from "lucide-react";
import { toast } from "sonner";

const PLAN_ORDER = [
  { key: "basic", label: "Creator" },
  { key: "pro", label: "Business" },
  { key: "pro_unlimited", label: "Business Unlimited" },
];

export default function AdminToolConfig() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingTool, setSavingTool] = useState(null);
  const [savingPlan, setSavingPlan] = useState(null);
  const [drafts, setDrafts] = useState({});         // per-tool credit_cost input
  const [planDrafts, setPlanDrafts] = useState({}); // per-plan credit cap

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tool-config");
      setItems(data.items || []);
      setPlans(data.plans || {});
      const d = {}; (data.items || []).forEach((i) => { d[i.tool] = String(i.credit_cost); });
      setDrafts(d);
      const pd = {};
      Object.entries(data.plans || {}).forEach(([k, v]) => { pd[k] = v.monthly_credits == null ? "" : String(v.monthly_credits); });
      setPlanDrafts(pd);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patchTool = async (tool, patch) => {
    setSavingTool(tool);
    try { await api.put(`/admin/tool-config/${tool}`, patch); toast.success(`Updated ${tool}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSavingTool(null); }
  };

  const resetTool = async (tool) => {
    if (!window.confirm(`Reset ${tool} to defaults? Any admin overrides will be removed.`)) return;
    try { await api.delete(`/admin/tool-config/${tool}`); toast.success(`Reset ${tool}`); await load(); }
    catch { toast.error("Reset failed"); }
  };

  const savePlanCap = async (planKey) => {
    setSavingPlan(planKey);
    const raw = planDrafts[planKey];
    const monthly_credits = raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0);
    try { await api.put(`/admin/plans/${planKey}/credit-cap`, { monthly_credits }); toast.success(`${PLAN_ORDER.find(p => p.key === planKey)?.label} cap updated`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSavingPlan(null); }
  };

  return (
    <div data-testid="admin-tool-config">
      <div className="dms-label mb-3">Access &amp; Credits</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Tool config &amp; Strategy Credits</h1>
      <p className="mt-2 text-[#475569] text-[14.5px]">Credit costs per tool, trial gating, and per-plan access. All changes are live.</p>

      {/* Plan credit caps */}
      <div className="mt-6 dms-card p-5 sm:p-6" data-testid="plan-credit-caps">
        <div className="flex items-center gap-2 mb-3">
          <Coins className="w-4 h-4 text-[#1D4E89]" />
          <div className="font-heading font-bold text-[#0A2342]">Monthly Strategy Credits per plan</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PLAN_ORDER.map((p) => (
            <div key={p.key}>
              <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] mb-2">{p.label}</div>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`plan-cap-${p.key}`}
                  value={planDrafts[p.key] ?? ""}
                  onChange={(e) => setPlanDrafts({ ...planDrafts, [p.key]: e.target.value.replace(/[^0-9]/g, "") })}
                  placeholder="unlimited"
                  className="dms-input w-28"
                />
                <button data-testid={`save-plan-cap-${p.key}`} onClick={() => savePlanCap(p.key)} disabled={savingPlan === p.key} className="dms-btn-primary text-[12.5px] py-2 px-3">
                  {savingPlan === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              </div>
              <div className="mt-1 text-[11px] text-[#94A3B8]">Leave blank for unlimited.</div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-tool config */}
      <div className="mt-6 space-y-3">
        {loading ? <div className="text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div> :
         items.map((t) => (
          <div key={t.tool} className="dms-card p-4 sm:p-5" data-testid={`tool-config-${t.tool}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-[180px]">
                <div className="font-mono text-[13px] text-[#0A2342] font-bold">{t.tool}</div>
                <div className="mt-1 text-[11px] text-[#94A3B8]">{t.has_override ? "custom override" : "using defaults"}</div>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] block mb-1">Credit cost</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0}
                      data-testid={`credit-cost-${t.tool}`}
                      value={drafts[t.tool] ?? ""}
                      onChange={(e) => setDrafts({ ...drafts, [t.tool]: e.target.value })}
                      className="dms-input w-20 text-[13.5px]"
                    />
                    <button data-testid={`save-cost-${t.tool}`} onClick={() => patchTool(t.tool, { credit_cost: parseInt(drafts[t.tool] || "0", 10) })} disabled={savingTool === t.tool} className="dms-btn-primary text-[12px] py-1.5 px-2.5">
                      {savingTool === t.tool ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] block mb-1">Trial access</label>
                  <label className="inline-flex items-center gap-2 text-[13px]">
                    <input data-testid={`trial-${t.tool}`} type="checkbox" checked={!!t.trial_allowed} onChange={(e) => patchTool(t.tool, { trial_allowed: e.target.checked })} />
                    {t.trial_allowed ? "Allowed on trial" : "Locked (Upgrade to Unlock)"}
                  </label>
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] block mb-1">Enabled</label>
                  <label className="inline-flex items-center gap-2 text-[13px]">
                    <input data-testid={`enabled-${t.tool}`} type="checkbox" checked={!!t.enabled} onChange={(e) => patchTool(t.tool, { enabled: e.target.checked })} />
                    {t.enabled ? "Live" : "Off"}
                  </label>
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] block mb-1">Plans</label>
                  <div className="flex gap-3">
                    {PLAN_ORDER.map((p) => (
                      <label key={p.key} className="inline-flex items-center gap-1 text-[12.5px]">
                        <input data-testid={`plan-${p.key}-${t.tool}`} type="checkbox" checked={!!t.plans?.[p.key]} onChange={(e) => patchTool(t.tool, { plans: { ...t.plans, [p.key]: e.target.checked } })} />
                        {p.label.split(" ")[0]}
                      </label>
                    ))}
                  </div>
                </div>
                <button data-testid={`reset-${t.tool}`} onClick={() => resetTool(t.tool)} className="dms-btn-ghost text-[12px] py-1.5 px-2.5" title="Reset to defaults"><RotateCcw className="w-3 h-3" /> Reset</button>
              </div>
            </div>
          </div>
         ))}
      </div>
    </div>
  );
}
